import type { IssueSLAsDto } from '@issuemanager/api-client';
import {
  ExtendedAutocomplete,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { useInfiniteIssueSLAs } from '@issuemanager/client/features/issues';
import { useMemo, useState } from 'react';
import type { MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export type TransformedIssueSLAsDto = IssueSLAsDto & {
  id: string;
};

export type IssueSLAsMultipleSelectorProps = MultipleSelectorProps<
  TransformedIssueSLAsDto[],
  TransformedIssueSLAsDto
>;

function issueSLADataTransformer(data: IssueSLAsDto) {
  return {
    ...data,
    id: String(data.slaDueDays),
  };
}

export function IssueSLAsMultipleSelector(
  props: IssueSLAsMultipleSelectorProps,
) {
  const { value: valueProp, onChange, enabled: _enabled, ...rest } = props;

  const value = useMemo(
    () => valueProp?.map(issueSLADataTransformer) ?? [],
    [valueProp],
  );

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value);

  const issueSLAsQuery = useInfiniteIssueSLAs({
    params: {
      filter: {
        searchTerm: filterByText,
      },
    },
    enabled: filterByText.length > 0,
  });

  const options = mergePaginatedDataAndValue(
    issueSLAsQuery.data?.pages.map((c) =>
      c.data.map(issueSLADataTransformer),
    ) ?? [],
    value,
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    query: issueSLAsQuery,
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
  });

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={sortOptionsBySelected(
        options,
        partialValue,
        autocompleteProps.isOptionEqualToValue,
      )}
      getOptionLabel={(option) => String(option.slaDueDays)}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
      {...rest}
    />
  );
}
